/**
 * TTS lesson audio chrome prototype.
 * Access: /?test=tts-lesson-audio
 *
 * Click through empty / generating / ready / stale / error / locker.
 * Player chrome lives in AudioFencePlayer (same widget production will use).
 * Generate audio lives in the document ... menu, next to Google Drive.
 *
 * Bet: docs/product/bets/2026-09-15-tts-lesson-audio-generate-store-play.md
 */
import { useEffect, useState } from 'react'
import {
  ExternalLink,
  Info,
  Loader2,
  MoreVertical,
  Volume2,
} from 'lucide-react'
import { AudioFencePlayer } from '@/components/features/audio-fence-player'
import { DocumentSoftBookmarkMenuSection } from '@/components/layout/document-soft-bookmark-menu'
import {
  ProtoAppHeader,
  ProtoIconRail,
  ProtoTitleRow,
} from '@/components/prototype/maater-proto-chrome'
import { maaterProtoAppSkinStyle } from '@/components/prototype/maater-proto-shell'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { IconButton } from '@/components/ui/icon-button'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { Toaster } from '@/components/ui/toaster'
import '@/components/features/document-reading-prose.css'

type SceneId = 'empty' | 'generating' | 'ready' | 'stale' | 'error' | 'locker'

const SCENES: { id: SceneId; label: string }[] = [
  { id: 'empty', label: 'Empty' },
  { id: 'generating', label: 'Generating' },
  { id: 'ready', label: 'Ready' },
  { id: 'stale', label: 'Stale' },
  { id: 'error', label: 'Error' },
  { id: 'locker', label: 'Locker MP3' },
]

const LESSON_TITLE = 'Session 001 - Logarithms and Decibels'
const PROTO_DOC_ID = 'tts-lesson-audio-proto'
const DURATION = 12 * 60 + 4

function formatClock(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const minutes = Math.floor(safe / 60)
  const seconds = safe % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export function TtsLessonAudioPrototype() {
  const [scene, setScene] = useState<SceneId>('empty')
  const [seconds, setSeconds] = useState(4 * 60 + 12)
  const [playing, setPlaying] = useState(false)
  const [resumeStamp, setResumeStamp] = useState<string | null>(null)

  useEffect(() => {
    if (scene === 'generating') {
      const timer = window.setTimeout(() => {
        setScene('ready')
        setSeconds(0)
        setPlaying(false)
      }, 1400)
      return () => window.clearTimeout(timer)
    }
    return undefined
  }, [scene])

  useEffect(() => {
    if (!playing || (scene !== 'ready' && scene !== 'locker')) return undefined
    const timer = window.setInterval(() => {
      setSeconds((current) => (current >= DURATION ? 0 : current + 1))
    }, 400)
    return () => window.clearInterval(timer)
  }, [playing, scene])

  const isLesson = scene !== 'locker'
  const showPlayer = scene === 'ready' || scene === 'stale' || scene === 'locker'
  const showError = scene === 'error'
  const isGenerating = scene === 'generating'
  const generateLabel = scene === 'empty' || scene === 'error' ? 'Generate audio' : 'Regenerate audio'

  const startGenerate = () => {
    setScene('generating')
    setPlaying(false)
  }

  return (
    <div
      className="flex h-screen flex-col bg-background text-foreground"
      style={maaterProtoAppSkinStyle()}
      data-testid={testIds.ttsLessonAudioProto.root}
    >
      <ProtoAppHeader
        title="Maater"
        subtitle="TTS lesson audio proto"
        themeControl="light-dark"
      />
      <div className="flex min-h-0 flex-1">
        <ProtoIconRail activeId="browse" />
        <div className="flex min-w-0 flex-1 flex-col">
          <div
            className="flex shrink-0 flex-wrap items-center gap-1 border-b px-3 py-2"
            style={{ borderColor: 'var(--line)' }}
            data-testid={testIds.ttsLessonAudioProto.sceneNav}
          >
            <span className="mr-2 text-[11px] text-muted-foreground">Scene</span>
            {SCENES.map((item) => (
              <Button
                key={item.id}
                type="button"
                variant={scene === item.id ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => {
                  setScene(item.id)
                  setPlaying(item.id === 'ready' || item.id === 'locker' ? false : playing)
                  if (item.id === 'ready' || item.id === 'stale') setSeconds(4 * 60 + 12)
                  if (item.id === 'locker') setSeconds(90)
                  if (item.id === 'empty' || item.id === 'error') setPlaying(false)
                }}
                data-testid={testIds.ttsLessonAudioProto.sceneButton(item.id)}
              >
                {item.label}
              </Button>
            ))}
          </div>

          <ProtoTitleRow
            density="inline"
            crumbs={
              isLesson
                ? ['Acoustics', 'Sessions', LESSON_TITLE]
                : ['Acoustics', 'Sessions', 'Session 001 spoken.mp3']
            }
            title={isLesson ? LESSON_TITLE : 'Session 001 spoken.mp3'}
            actions={
              <div className="flex items-center gap-1">
                <IconButton
                  icon={<Info className="h-4 w-4" />}
                  label="Toggle info pane"
                  variant="ghost"
                  size="sm"
                  data-testid={testIds.documentViewer.infoButton}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      icon={<MoreVertical className="h-4 w-4" />}
                      label="More options"
                      variant="ghost"
                      size="sm"
                      data-testid={testIds.documentViewer.menuButton}
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                    className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
                  >
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger data-testid={testIds.documentViewer.gdocMenuSubTrigger}>
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Google Drive
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent
                        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
                        className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
                      >
                        <DropdownMenuItem disabled>
                          Sync with Drive
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled>
                          Keep synced
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    {isLesson ? (
                      <DropdownMenuItem
                        disabled={isGenerating}
                        onClick={startGenerate}
                        data-testid={testIds.documentViewer.generateAudio}
                      >
                        {isGenerating ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Volume2 className="mr-2 h-4 w-4" />
                        )}
                        {generateLabel}
                      </DropdownMenuItem>
                    ) : null}
                    <DocumentSoftBookmarkMenuSection documentId={PROTO_DOC_ID} />
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            }
          />

          <div
            className={cn(
              'document-reading-prose min-h-0 flex-1 overflow-y-auto px-6 py-5',
              'mx-auto w-full max-w-3xl',
            )}
            data-testid={testIds.ttsLessonAudioProto.lessonBody}
          >
            {scene === 'empty' ? (
              <p className="mb-4 text-xs text-muted-foreground">
                No audio fence yet. Generate audio is in the document ... menu, next to Google Drive.
              </p>
            ) : null}

            {showError ? (
              <AudioFencePlayer
                error="xAI TTS returned 429. The lesson is unchanged - no broken player."
                currentSeconds={0}
                durationSeconds={DURATION}
                onRegenerate={() => setScene('generating')}
              />
            ) : null}

            {isGenerating ? (
              <AudioFencePlayer
                isGenerating
                currentSeconds={0}
                durationSeconds={DURATION}
              />
            ) : null}

            {showPlayer ? (
              <AudioFencePlayer
                voiceId="eve"
                currentSeconds={seconds}
                durationSeconds={DURATION}
                isPlaying={playing}
                isStale={scene === 'stale'}
                onTogglePlay={() => {
                  setPlaying((value) => {
                    if (value) setResumeStamp(formatClock(seconds))
                    return !value
                  })
                }}
                onSeek={setSeconds}
                onDownload={() =>
                  toast.message('Download', {
                    description: 'Same MP3. Files, then BookPlayer. Maater does not play in the car.',
                  })
                }
                onRegenerate={isLesson ? () => setScene('generating') : undefined}
              />
            ) : null}

            {resumeStamp ? (
              <p className="mb-4 text-xs text-muted-foreground">
                Resume {resumeStamp} on this browser. Same localStorage store as ... Bookmark
                (not written into the shared lesson).
              </p>
            ) : null}

            {isLesson ? (
              <>
                <h2 className="mb-2 text-base font-semibold">Why a ratio is not a decibel</h2>
                <p className="mb-3 text-sm leading-relaxed text-foreground/90">
                  Suppose amplifier A produces two hundred watts and amplifier B produces fifty
                  watts. The meaningful comparison is two hundred divided by fifty, which equals
                  four. A decibel does not contain watts. It expresses that ratio.
                </p>
                <p className="text-sm leading-relaxed text-foreground/90">
                  Play here on wifi. Download when you want the car. Assessment stays a later
                  session - this player does not quiz you.
                </p>
              </>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">
                Opening the locker file skips Generate audio. Same player. Download still hands
                the file to BookPlayer.
              </p>
            )}
          </div>
        </div>
      </div>
      <Toaster />
    </div>
  )
}
